import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../Header';

/**
 * WEB DESIGN & DEVELOPMENT PAGE
 * Theme: 60% Aqua Haze (#F0F4F8) | 30% River Bed (#4A5568) | 10% Accent (#2B6CB0)
 * Headlines: #020617 (High Contrast)
 */
const WebDesignDevelopment = () => {
  return (
    <div className="min-h-screen bg-[#F0F4F8] text-[#4A5568] font-sans">
      <Header />
      
      {/* 1. HERO SECTION - Technical Authority */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#2B6CB0] font-black text-[10px] uppercase tracking-[0.4em] mb-6">Performance & Scale</p>
            <h1 className="text-5xl md:text-8xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.9]">
              Web Design <br className="hidden md:block" /> 
              <span className="text-[#2B6CB0]">&</span> Development
            </h1>
            <p className="text-xl text-[#4A5568] max-w-3xl mx-auto mb-12 leading-relaxed font-medium">
              We build digital interfaces for teams that demand more than just a "pretty" site. 
              Caffryx delivers high-conversion corporate websites and custom product experiences designed for long-term growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/contact" className="bg-[#2B6CB0] text-white px-10 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-[#020617] transition-all shadow-xl shadow-[#2B6CB0]/20">
                Start Your Project
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-8 h-[1px] bg-[#4A5568]"></div>
                <span className="text-[#4A5568] text-[10px] font-black uppercase tracking-widest">
                  Performance-First Architecture
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. OUR APPROACH - Visual Logic */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-32">
            <div>
              <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.85]">
                Engineering <br/> <span className="text-[#2B6CB0]">Conversion</span>
              </h2>
              <p className="text-lg text-[#4A5568] mb-8 leading-relaxed font-medium">
                Every project blends UX strategy, editorial-grade visuals, and modern front-end engineering. 
                Our pages load faster, stay accessible, and are optimized for SEO from the very first commit.
              </p>
              <div className="space-y-4">
                 <p className="flex items-center gap-4 text-xs font-black uppercase tracking-widest text-[#020617]">
                    <span className="w-2 h-2 bg-[#2B6CB0] rounded-full"></span> Michigan Based Hubs
                 </p>
                 <p className="flex items-center gap-4 text-xs font-black uppercase tracking-widest text-[#020617]">
                    <span className="w-2 h-2 bg-[#2B6CB0] rounded-full"></span> Full-Stack Scalability
                 </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full border-2 border-[#2B6CB0]/20 rounded-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"
                alt="Web Design & Development"
                className="relative w-full h-[450px] object-cover rounded-2xl shadow-2xl grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>
{/* 3. TECH STACK - Agency Capabilities */}
<div className="mb-32">
  <h2 className="text-2xl font-black mb-12 tracking-widest text-[#020617] uppercase border-b-4 border-[#2B6CB0] inline-block pb-2">
    Our Stack
  </h2>

  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
    {[
      'Web Design & UX',
      'Front-End Development',
      'Back-End Systems',
      'E-Commerce Solutions',
      'CMS Platforms',
      'SEO Optimization',
      'Performance Optimization',
      'Cloud & Hosting',
      'API Integrations',
      'Automation',
      'Analytics & Tracking',
      'Security & Maintenance'
    ].map((tech) => (
      <div
        key={tech}
        className="bg-white p-6 rounded-xl border border-[#F0F4F8] shadow-sm text-center group hover:bg-[#2B6CB0] transition-colors duration-300"
      >
        <h3 className="text-[11px] font-black uppercase tracking-widest text-[#2B6CB0] group-hover:text-white transition-colors">
          {tech}
        </h3>
      </div>
    ))}
  </div>
</div>

          {/* 4. PROCESS - The System */}
          <div className="mb-32">
            <h2 className="text-4xl md:text-5xl font-black text-center mb-20 tracking-tighter text-[#020617] uppercase">The <span className="text-[#2B6CB0]">Build System</span></h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 relative">
              {[
                { step: '01', title: 'Discovery', desc: 'Analyzing business goals, audience behavior, and technical requirements.' },
                { step: '02', title: 'Interface', desc: 'Designing high-fidelity prototypes and fluid user experience flows.' },
                { step: '03', title: 'Engineering', desc: 'Clean, modular code built for speed, responsiveness, and scale.' },
                { step: '04', title: 'Deployment', desc: 'Rigorous testing, optimized launch, and continuous technical support.' }
              ].map((item) => (
                <div key={item.step} className="relative group">
                  <span className="text-7xl font-black text-[#F0F4F8] absolute -top-12 left-0 z-0 group-hover:text-[#2B6CB0]/10 transition-colors">
                    {item.step}
                  </span>
                  <div className="relative z-10 pt-4">
                    <h3 className="text-sm font-black mb-3 text-[#020617] uppercase tracking-widest">{item.title}</h3>
                    <p className="text-[#4A5568] text-[13px] font-medium leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA Card */}
          <div className="text-center bg-[#020617] p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-full h-1 bg-[#2B6CB0]"></div>
            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-white uppercase leading-tight">
              Build Something <br className="hidden md:block" /> Substantial
            </h2>
            <p className="text-xl text-white/60 mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
              Let's audit your current digital presence and engineer a solution that actually drives your business forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-[#2B6CB0] text-white px-12 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all shadow-xl">
                Get Started
              </Link>
              <Link to="/services" className="border-2 border-[#2B6CB0] text-[#2B6CB0] px-12 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-[#2B6CB0] hover:text-white transition-all">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDesignDevelopment;